import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import cx from './cx';

const SIZES = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl', full: 'sm:max-w-[min(96vw,1400px)]' };
const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

let openCount = 0;
// Stack of open modal ids; only the topmost handles Escape/Tab.
const modalStack = [];

/**
 * Dialog with focus trap, Escape to close, scroll lock and focus restore.
 * Renders as a bottom sheet under 640px and a centred dialog above.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  children,
  dismissible = true,
  initialFocusRef,
  className,
  bodyClassName,
  hideTitle = false,
  closeLabel = 'Close',
}) {
  const panelRef = useRef(null);
  const titleId = useId();
  const descId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const stackId = Symbol('modal');
    modalStack.push(stackId);
    openCount += 1;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTarget = initialFocusRef?.current || panelRef.current?.querySelector('[data-autofocus]') || panelRef.current?.querySelector(FOCUSABLE) || panelRef.current;
    const raf = requestAnimationFrame(() => focusTarget?.focus?.());

    const onKeyDown = (event) => {
      if (!panelRef.current || modalStack[modalStack.length - 1] !== stackId) return;
      if (event.key === 'Escape' && dismissible) {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter((node) => node.offsetParent !== null || node === document.activeElement);
      if (nodes.length === 0) {
        event.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      const index = modalStack.indexOf(stackId);
      if (index >= 0) modalStack.splice(index, 1);
      openCount -= 1;
      if (openCount === 0) document.body.style.overflow = previousOverflow;
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [open, dismissible, initialFocusRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center sm:p-4">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={dismissible ? () => onCloseRef.current?.() : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cx(
          'relative flex max-h-[92vh] w-full flex-col overflow-hidden border border-line bg-surface-raised text-fg shadow-pop outline-none',
          'rounded-t-panel sm:rounded-panel',
          SIZES[size] || SIZES.md,
          className
        )}
      >
        {(title || dismissible) && (
          <div className={cx('flex items-start justify-between gap-3 px-5 pt-4', hideTitle ? 'pb-0' : 'pb-3')}>
            <div className="min-w-0">
              {title && <h2 id={titleId} className={cx('text-base font-semibold text-fg', hideTitle && 'sr-only')}>{title}</h2>}
              {description && <p id={descId} className="mt-1 text-sm text-fg-muted">{description}</p>}
            </div>
            {dismissible && (
              <button
                type="button"
                onClick={() => onCloseRef.current?.()}
                aria-label={closeLabel}
                className="-me-2 -mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-fg-muted hover:bg-surface-2 hover:text-fg"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        <div className={cx('min-h-0 flex-1 overflow-y-auto px-5 pb-5', bodyClassName)}>{children}</div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-line bg-surface px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
